import { escapeHtml, getSafeHttpUrl, getSafeTelUrl } from "./_template-safety.ts";

/**
 * Renders the "Email Signature" content block available on all report email
 * templates (conversation report, customer report, meeting recap, escalation
 * alert, conversation digest, view report). Mirrors HG Voice's BrandingEditor
 * preview (src/components/branding/signaturePreview.ts) and HG Mail's reply
 * signature (Help-Genie-Mail/lib/email/signature.ts) — all three read the same
 * `branding.signatureStyle` shape so a genie's signature looks identical
 * everywhere it appears.
 *
 * Deliberately reads ONLY the passed-in `branding` object — no live lookups.
 * Callers must resolve branding (agent branding, falling back to team
 * branding) before invoking this.
 */

export interface SignatureStyle {
  enabled: boolean;
  layout: "standard" | "compact" | "centered";
  showDescription: boolean;
  showMascot: boolean;
  showQrCode: boolean;
  showChannelBadges: boolean;
  textOverride?: string | null;
  phoneNumber?: string | null;
  liveUrl?: string | null;
}

export interface SignatureBranding {
  primaryColor?: string | null;
  secondaryColor?: string | null;
  gradientEnabled?: boolean;
  gradientAngle?: number | null;
  logoUrl?: string | null;
  customAvatarUrl?: string | null;
  signatureStyle?: SignatureStyle | null;
}

const INK = "#101828";
const MUTED = "#5B6779";
const BORDER = "#E3E8EF";
const UNBRANDED = "#344054";
const FONT =
  "'Poppins','Poppins Regular',-apple-system,BlinkMacSystemFont,'Segoe UI','Helvetica Neue',Helvetica,Arial,sans-serif";
const AVATAR_TINT = 0.28;

// Same Supabase Storage bucket every other report template's static assets
// (wordmark, mascots, app-store badges) already load from — not a made-up
// helpgenie.ai path with no backing route or asset.
const ASSET_BASE = "https://vdskjkjxyeehklwxnhrf.supabase.co/storage/v1/object/public/email-assets";

// This repo has no public, unauthenticated QR-image endpoint of its own (the
// internal `qr-code` API action requires auth and returns JSON, not an image).
// api.qrserver.com is the same public QR generator already used elsewhere in
// this app for preview/mock data (src/hooks/useEmailTemplates/
// useEmailTemplatePreview.ts, src/components/agent/QRCodeModal.tsx).
function qrImageUrl(targetUrl: string, sizePx: number): string {
  return `https://api.qrserver.com/v1/create-qr-code/?size=${sizePx}x${sizePx}&data=${encodeURIComponent(targetUrl)}`;
}

function safeBrandColor(value: string | null | undefined): string | null {
  const trimmed = value?.trim();
  if (!trimmed) return null;
  const short = /^#([0-9a-fA-F])([0-9a-fA-F])([0-9a-fA-F])$/.exec(trimmed);
  if (short) return `#${short[1]}${short[1]}${short[2]}${short[2]}${short[3]}${short[3]}`;
  return /^#[0-9a-fA-F]{6}$/.test(trimmed) ? trimmed : null;
}

function tintToWhite(color: string, strength: number): string {
  return `#${[1, 3, 5]
    .map((index) => Number.parseInt(color.slice(index, index + 2), 16))
    .map((channel) => Math.round(channel * strength + 255 * (1 - strength)))
    .map((channel) => channel.toString(16).padStart(2, "0"))
    .join("")}`;
}

function hostLabel(url: string): string {
  try {
    return new URL(url).host.replace(/^www\./, "");
  } catch {
    return url;
  }
}

interface SignatureChannel {
  label: string;
  value: string | null;
  href: string;
}

function buildChannels(genieName: string, style: SignatureStyle): SignatureChannel[] {
  const channels: SignatureChannel[] = [];
  const liveUrl = getSafeHttpUrl(style.liveUrl ?? "");
  if (liveUrl) channels.push({ label: "Chat", value: `Talk to ${genieName}`, href: liveUrl });

  const telUrl = style.phoneNumber ? getSafeTelUrl(style.phoneNumber) : null;
  if (telUrl && style.phoneNumber) channels.push({ label: "Call", value: style.phoneNumber, href: telUrl });

  if (liveUrl) channels.push({ label: "Web", value: hostLabel(liveUrl), href: liveUrl });
  return channels;
}

/**
 * @param genieName Display name — always required, even when the signature is
 *   otherwise unbranded.
 * @param description Genie description (or block-level textOverride, already
 *   resolved by the caller) to show under the name, when enabled.
 * @param branding Resolved GenieBranding — the agent's own, or team branding
 *   when there is no single genie (e.g. view_report).
 */
export function renderSignatureBlockHtml(
  genieName: string,
  description: string | null | undefined,
  branding: SignatureBranding | null | undefined,
): string {
  const style = branding?.signatureStyle;
  if (!style || !style.enabled) return "";

  const name = genieName.trim() || "Your Genie";
  const brandColor = safeBrandColor(branding?.primaryColor);
  const primary = brandColor ?? UNBRANDED;
  const secondary = safeBrandColor(branding?.secondaryColor) ?? primary;
  const angle =
    typeof branding?.gradientAngle === "number" && Number.isFinite(branding.gradientAngle)
      ? Math.max(0, Math.min(360, branding.gradientAngle))
      : 135;
  const useGradient = Boolean(branding?.gradientEnabled) && Boolean(brandColor) && secondary !== primary;
  const gradient = useGradient ? `linear-gradient(${angle}deg,${primary},${secondary})` : "none";
  const logoUrl = getSafeHttpUrl(branding?.logoUrl || branding?.customAvatarUrl || "");

  const avatarFill = tintToWhite(primary, AVATAR_TINT);
  const avatarGradient = useGradient
    ? `linear-gradient(${angle}deg,${avatarFill},${tintToWhite(secondary, AVATAR_TINT)})`
    : "none";
  const chipFill = tintToWhite(primary, 0.07);
  const chipBorder = tintToWhite(primary, 0.22);

  const showArt = style.layout !== "compact";
  const centered = style.layout === "centered";
  const channels = style.showChannelBadges ? buildChannels(name, style) : [];

  const rawDescription = style.showDescription ? style.textOverride?.trim() || description?.trim() : "";
  const shortDescription =
    rawDescription && rawDescription.length > 110 ? `${rawDescription.slice(0, 107).trimEnd()}...` : rawDescription;

  const logo = logoUrl
    ? `<td width="74" valign="middle" style="padding:0 16px 0 0"><div style="width:58px;height:58px;border-radius:29px;background-color:${avatarFill};background-image:${avatarGradient};text-align:center;font-size:0;line-height:0"><img src="${escapeHtml(logoUrl)}" width="58" height="58" alt="${escapeHtml(name)}" style="display:block;width:58px;height:58px;border:0;border-radius:29px;object-fit:contain"></div></td>`
    : "";

  const mascotUrl = showArt && style.showMascot ? `${ASSET_BASE}/mascot-thumbsup.png` : null;
  const mascot = mascotUrl
    ? `<td width="12%" valign="middle" align="right" style="width:12%;box-sizing:border-box;padding:0 2px 0 16px;font-size:0;line-height:0"><img src="${escapeHtml(mascotUrl)}" width="86" height="86" alt="Help Genie" style="display:block;width:100%;max-width:86px;height:auto;border:0"></td>`
    : "";

  const safeLiveUrl = style.liveUrl ? getSafeHttpUrl(style.liveUrl) : null;
  const qrUrl = showArt && style.showQrCode && safeLiveUrl ? qrImageUrl(safeLiveUrl, 132) : null;
  const qr = qrUrl
    ? `<td width="10%" valign="middle" align="right" style="width:10%;box-sizing:border-box;padding-left:14px"><img src="${escapeHtml(qrUrl)}" width="66" height="66" alt="Scan to chat with ${escapeHtml(name)}" style="display:block;width:100%;max-width:66px;height:auto;border:1px solid ${BORDER};border-radius:10px"><div style="margin-top:5px;font-family:${FONT};color:${MUTED};font-size:9px;font-weight:500;line-height:12px;text-align:center;white-space:nowrap">Scan to chat</div></td>`
    : "";

  const channelRow = channels.length
    ? channels
        .map((channel) => {
          const value = channel.value
            ? ` <span style="color:${MUTED};font-weight:500">${escapeHtml(channel.value)}</span>`
            : "";
          return `<a href="${escapeHtml(channel.href)}" style="display:inline-block;max-width:100%;box-sizing:border-box;margin:0 8px 8px 0;padding:7px 14px;border-radius:999px;background-color:${chipFill};border:1px solid ${chipBorder};font-family:${FONT};color:${INK};font-size:12px;font-weight:600;line-height:16px;text-decoration:none;white-space:normal;overflow-wrap:anywhere;word-break:break-word"><span style="color:${primary};font-weight:700">${escapeHtml(channel.label)}</span>${value}</a>`;
        })
        .join("")
    : "";

  const nameAlign = centered ? "center" : "left";
  const descriptionRow = shortDescription
    ? `<div style="margin-top:3px;font-family:${FONT};color:${MUTED};font-size:12.5px;font-weight:400;line-height:18px">${escapeHtml(shortDescription)}</div>`
    : "";
  const nameCell = `<td valign="middle" align="${nameAlign}"><div style="font-family:${FONT};color:${INK};font-size:18px;font-weight:700;line-height:24px">${escapeHtml(name)}<span style="display:inline-block;margin-left:8px;padding:3px 9px;border-radius:999px;background-color:${tintToWhite(primary, 0.13)};font-family:${FONT};color:${primary};font-size:9px;font-weight:700;letter-spacing:.1em;line-height:12px;text-transform:uppercase;vertical-align:middle">Genie</span></div>${descriptionRow}</td>`;
  const bodyRow = centered ? `<tr>${nameCell}</tr>` : `<tr>${logo}${nameCell}${mascot}${qr}</tr>`;

  return `<table role="presentation" cellpadding="0" cellspacing="0" border="0" width="100%" style="width:100%;margin-top:24px;border-collapse:separate;border-spacing:0;font-family:${FONT}"><tr><td height="4" bgcolor="${primary}" style="height:4px;background-color:${primary};background-image:${gradient};font-size:0;line-height:0;border-radius:16px 16px 0 0">&nbsp;</td></tr><tr><td style="padding:18px 20px;background-color:#FFFFFF;border:1px solid ${BORDER};border-top:0;border-radius:0 0 16px 16px"><table role="presentation" cellpadding="0" cellspacing="0" border="0" width="100%" style="table-layout:auto">${bodyRow}</table>${channelRow ? `<div style="margin-top:15px;padding-top:13px;border-top:1px solid ${BORDER};text-align:${nameAlign}">${channelRow}</div>` : ""}<div style="margin-top:${channelRow ? "8" : "15"}px;font-family:${FONT};color:#98A2B3;font-size:10.5px;font-weight:400;line-height:15px;text-align:${nameAlign}"><span style="font-family:${FONT};color:${primary};font-weight:700">Powered by Help Genie</span> &middot; one Genie, every channel</div></td></tr></table>`;
}
